import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { clients as clientsApi } from '@/api/endpoints'
import { EmptyState, Spinner } from '@/components/ui'
import Pagination from '@/components/ui/Pagination'
import ClientCredit from '@/features/clients/ClientCredit'
import { useCodebooks } from '@/hooks/useBootstrap'
import { formatDate, formatDateTime, formatMoney } from '@/lib/utils'
import type { ClientReservation, Paginated } from '@/types/api'
import { paymentStatusLabel } from '@/lib/paymentStatus'

export default function ClientDetailPage() {
  const { t } = useTranslation()
  const books = useCodebooks()
  const { id } = useParams<{ id: string }>()
  const clientId = Number.parseInt(id ?? '0', 10)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.get(clientId),
    enabled: Boolean(clientId),
  })
  const { data: reservations } = useQuery({
    queryKey: ['client-reservations', clientId, page, pageSize],
    queryFn: () => clientsApi.reservations(clientId, page, pageSize),
    enabled: Boolean(clientId),
    placeholderData: keepPreviousData,
  })
  const { data: money } = useQuery({
    queryKey: ['client-money', clientId],
    queryFn: () => clientsApi.money(clientId),
    enabled: Boolean(clientId),
  })

  const paged = reservations as Paginated<ClientReservation> | undefined
  const rows: ClientReservation[] = Array.isArray(reservations)
    ? reservations
    : (paged?.results ?? [])
  const count = Array.isArray(reservations) ? reservations.length : (paged?.count ?? 0)

  if (isLoading || !client) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  const totalSpent = Number.parseFloat(money?.charged ?? '0')
  const paid = Number.parseFloat(money?.paid ?? '0')
  const unpaid = Number.parseFloat(money?.due ?? '0')

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/clients" className="btn-ghost p-1.5">
          <ArrowLeft size={16} />
        </Link>
        <h1 className="text-lg font-semibold">{client.full_name}</h1>
        {client.is_children ? (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{t('global.child')}</span>
        ) : null}
        {client.is_beginner ? (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{t('global.beginner')}</span>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label={t('reservation.phone')} value={client.tel || '—'} />
        <Stat label={t('reservation.email')} value={client.email || '—'} />
        <Stat label={t('clients.totalSpent')} value={formatMoney(totalSpent)} />
        <Stat label={t('clients.paid')} value={formatMoney(paid)} />
        <Stat label={t('clients.unpaid')} value={formatMoney(unpaid)} highlight={unpaid > 0} />
        <Stat label={t('clients.level')} value={books.label('levels', client.levelid) || '—'} />
        <Stat
          label={t('reservation.language')}
          value={books.label('languages', client.lang_id) || '—'}
        />
        <Stat label={t('reservation.course')} value={books.label('courses', client.course_id) || '—'} />
        <Stat label={t('clients.created')} value={formatDate(client.created) || '—'} />
      </div>

      <ClientCredit clientId={client.id} />

      {client.note ? (
        <div className="card p-3 text-sm">
          <div className="mb-1 text-xs font-medium uppercase text-slate-500">{t('global.note')}</div>
          {client.note}
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        <h2 className="border-b border-slate-200 px-3 py-2 text-sm font-semibold">
          {t('clients.reservations')}
        </h2>
        {rows.length === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <>
            <table className="table-base">
              <thead>
                <tr>
                  <th>{t('reservation.date')}</th>
                  <th>{t('reservation.instructor')}</th>
                  <th>{t('reservation.course')}</th>
                  <th>{t('reservation.language')}</th>
                  <th className="text-right">{t('reservation.persons')}</th>
                  <th className="text-right">{t('reservation.price')}</th>
                  <th>{t('reservation.status')}</th>
                  <th>{t('reservation.paid')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap">{formatDateTime(r.start)}</td>
                    <td>{r.instructor_name || '—'}</td>
                    <td>{books.label('courses', r.course_id) || '—'}</td>
                    <td>{books.label('languages', r.lang_id) || '—'}</td>
                    <td className="text-right tabular-nums">{r.numbers}</td>
                    <td className="text-right tabular-nums">{formatMoney(r.fees_total)}</td>
                    <td>{books.label('statuses', r.status_id) || '—'}</td>
                    <td>{paymentStatusLabel(t, r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              page={page}
              pages={Math.max(1, Math.ceil(count / pageSize))}
              pageSize={pageSize}
              count={count}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </>
        )}
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div className="card p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={highlight ? 'text-lg font-semibold text-red-600' : 'text-lg font-semibold'}>
        {value}
      </div>
    </div>
  )
}
